import { ref, computed, type MaybeRefOrGetter, toValue } from "vue";
import UsersService from "@/services/v1/Users.service.js";
import AuthService from "@/services/Auth.service";
import {
	useAppLocalStorage,
	useAppSessionStorage,
	type UserObject
} from "./useAppStorage";

import router from "@/router";

function hasUserInHistory(history: UserObject[], user: UserObject) {
	return history.some((h) => h.id === user.id);
}

export function useImpersonation() {
	const impersonationHistory = useAppLocalStorage(
		"IMPERSONATION_HISTORY",
		[]
	);
	const impersonationPossible = ref<UserObject[]>([]);
	const impersonatedUser = useAppSessionStorage(
		"IMPERSONATE_USER",
		null,
		true
	);

	async function loadImpersonation() {
		try {
			const possibleImpersonations =
				await UsersService.getSupportAccessGrants();
			impersonationPossible.value = possibleImpersonations;
			if (
				impersonatedUser.value &&
				!hasUserInHistory(
					impersonationHistory.value,
					impersonatedUser.value
				)
			) {
				impersonatedUser.value = null;
			}
		} catch {
			impersonationPossible.value = [];
		}
	}

	function addUserToHistory(user: UserObject) {
		const existingUserIndex = impersonationHistory.value.findIndex(
			(h) => h.id === user.id
		);
		if (existingUserIndex !== -1) {
			impersonationHistory.value.splice(existingUserIndex, 1);
		}
		impersonationHistory.value.unshift(user);
	}

	async function impersonateAs(user: MaybeRefOrGetter<UserObject>) {
		localStorage.removeItem("last-selected-organizationId");
		impersonatedUser.value = toValue(user);
		await AuthService.getUserInfo();
		addUserToHistory(toValue(user));
		router.push({ name: "organization-view" });
	}

	function endImpersonation(redirect: boolean = true) {
		impersonatedUser.value = null;
		if (redirect) router.push({ name: "user-delegated-connections" });
	}

	return {
		impersonationPossible: computed(() =>
			impersonationPossible.value.sort((a, b) => {
				const indexAInHistory = impersonationHistory.value.findIndex(
					(h) => h.id === a.id
				);
				const indexBInHistory = impersonationHistory.value.findIndex(
					(h) => h.id === b.id
				);
				if (indexAInHistory >= 0 && indexBInHistory < 0) return -1;
				if (indexBInHistory >= 0 && indexAInHistory < 0) return 1;
				return indexAInHistory - indexBInHistory;
			})
		),
		loadImpersonation,
		impersonateAs,
		impersonatedUser: computed(() => impersonatedUser.value),
		endImpersonation
	};
}
